<template>
    <div>
        <modal
            :id="modalId"
            title="Create Airbnb Property"
            ok-title="Create"
            :ok-disabled="
                loading || (createPropertyMode === 'LINK' && !selectedProperty)
            "
            @show="fetchAvailableProperties"
            @ok="handleCreatePropertyClick"
        >
            <div class="funkyradio">
                <div class="funkyradio-primary">
                    <input
                        type="radio"
                        value="NEW"
                        v-model="createPropertyMode"
                        id="create-property-airbnb"
                    />
                    <label for="create-property-airbnb"
                        >Create new Airbnb listing</label
                    >
                </div>

                <div class="funkyradio-success">
                    <input
                        type="radio"
                        value="LINK"
                        v-model="createPropertyMode"
                        id="link-property-airbnb"
                    />
                    <label for="link-property-airbnb"
                        >Link to existing Airbnb listing</label
                    >
                    <b-form-select
                        class="mt-1 mb-2"
                        v-model="synchronizationCategory"
                        v-if="
                            createPropertyMode == 'LINK' &&
                            availableProperties.length > 0
                        "
                    >
                        <option value="sync_all">Sync Everything</option>
                        <option value="sync_rates_and_availability">
                            Limited Sync
                        </option>
                    </b-form-select>
                    <b-form-select
                        v-model="selectedProperty"
                        v-if="
                            createPropertyMode == 'LINK' &&
                            availableProperties.length > 0
                        "
                    >
                        <option
                            v-for="item in availableProperties"
                            :key="item.id"
                            :value="item.id"
                        >
                            {{ item.name }}
                        </option>
                    </b-form-select>
                    <span v-if="loading && createPropertyMode == 'LINK'"
                        >Loading remote listings. Please wait ...</span
                    >
                    <span
                        v-else-if="
                            availableProperties.length == 0 &&
                            availableProperties.length != null &&
                            createPropertyMode == 'LINK'
                        "
                        >No listings available to link.</span
                    >
                </div>
            </div>
        </modal>
        <modal
            :id="createNewPropertyConfirmId"
            title="New Airbnb Listing Confirm"
            ok-title="Proceed"
            @ok="handleCreateProperty"
        >
            <div class="mb-2">
                You are creating a NEW listing on Airbnb, NOT linking to an
                existing listing. If you want to map to an existing listing, you
                must choose the "Link to Existing Airbnb Listing" option.
                <br /><br />
                What data should Lodgix sync with the new Airbnb listing after the listing is live on Airbnb?
            </div>
            <b-form-radio
                class="mb-2"
                v-model="synchronizationCategory"
                name="synchronization-category"
                value="sync_all"
            >
                <div>Sync Everything (recommended)</div>
                <div class="text-secondary">
                    Sync all listing details, booking settings, pricing, availability, etc., with Lodgix.
                </div>
            </b-form-radio>
            <b-form-radio
                v-model="synchronizationCategory"
                name="synchronization-category"
                value="sync_rates_and_availability"
            >
                <div>Limited Sync</div>
                <div class="text-secondary">
                    Sync only pricing and availability with Lodgix.
                    All other listing content will be managed on Airbnb.
                </div>
            </b-form-radio>
        </modal>
        <busy-modal :show="loading"></busy-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import API from '../../../api';
import {
    PARTNER_AIRBNB_FEED_SHARE_SYNC_PROPERTY_ENDPOINT,
    PARTNER_AIRBNB_FEED_SHARE_LINK_PROPERTY_ENDPOINT,
    PARTNER_AIRBNB_AVAILABLE_PROPERTIES_ENDPOINT,
} from '../../../endpoints';
import Modal from '../../generic/modal/modal.vue';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import { formatResponseError } from '../../../utils/api';

export default {
    components: { Modal, BusyModal },
    props: { modalId: String, feedShareId: Number },
    data() {
        return {
            loading: false,
            createPropertyMode: 'NEW',
            synchronizationCategory: 'sync_all',
            createNewPropertyConfirmId: 'airbnb-create-new-property-confirm',
            availableProperties: [],
            selectedProperty: null,
        };
    },
    methods: {
        handleCreatePropertyClick() {
            if (this.createPropertyMode === 'NEW') {
                this.synchronizationCategory = 'sync_all';
                this.$bvModal.show(this.createNewPropertyConfirmId);
            } else {
                this.handleLinkProperty();
            }
        },
        async handleCreateProperty() {
            this.loading = true;
            try {
                const { data } = await API.post(
                    PARTNER_AIRBNB_FEED_SHARE_SYNC_PROPERTY_ENDPOINT(this.feedShareId),
                    {
                        synchronization_category: this.synchronizationCategory,
                    }
                );
                this.successToast({ text: 'Airbnb property was created.' });
                this.$emit('update', data);
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({
                    text: generic || `Could not create property.`,
                });
                this.$emit('refresh', this.feedShareId);
            } finally {
                this.loading = false;
            }
        },
        async handleLinkProperty() {
            this.loading = true;
            try {
                await API.post(
                    PARTNER_AIRBNB_FEED_SHARE_LINK_PROPERTY_ENDPOINT(this.feedShareId),
                    {
                        synchronization_category: this.synchronizationCategory,
                        remote_id: this.selectedProperty,
                    }
                );
                this.successToast({ text: 'Airbnb property was linked.' });
                this.$emit('refresh', this.feedShareId);
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({
                    text: generic || `Could not link property.`,
                });
                this.$emit('refresh', this.feedShareId);
            } finally {
                this.loading = false;
            }
        },
        async fetchAvailableProperties() {
            this.loading = true;
            try {
                const { data } = await API.get(
                    PARTNER_AIRBNB_AVAILABLE_PROPERTIES_ENDPOINT()
                );
                this.availableProperties = [
                    { id: null, name: 'Please select a listing' },
                    ...data
                ];
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({
                    text: generic || `Could not load available properties.`,
                });
            } finally {
                this.loading = false;
            }
        },
    },
};
</script>
